import React, { useState } from 'react';
import { 
  Button, 
  Card, 
  Typography, 
  Space, 
  Empty, 
  Pagination, 
  Dropdown,
  Modal,
  message,
  Tooltip,
  Tag,
  Checkbox,
  Select,
  Table
} from 'antd';
import { 
  PlusOutlined, 
  UnorderedListOutlined, 
  ClockCircleOutlined, 
  FolderOutlined, 
  EllipsisOutlined, 
  EditOutlined, 
  DeleteOutlined,
  HistoryOutlined,
  CopyOutlined,
  CheckOutlined,
  ImportOutlined,
  AppstoreOutlined,
  StarOutlined,
  StarFilled
} from '@ant-design/icons';

import ImportPrompts from './ImportPrompts';

const { Title, Paragraph } = Typography;

// 提示词列表组件，展示所有可用的提示词卡片
const PromptList = ({ 
  prompts, 
  onSelectPrompt, 
  onCreatePrompt, 
  onDeletePrompt, 
  folders = [], 
  onMoveToFolder, 
  tags = [], 
  onAddFolder, 
  onAddTag,
  onBatchDeletePrompts,
  onFavoriteChange
}) => {
  // 用于跟踪哪些提示词已被复制，显示复制成功图标
  const [copiedPromptIds, setCopiedPromptIds] = useState({});
  // 状态管理
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9); // 默认每页显示9条
  const [viewMode, setViewMode] = useState('card'); // 'card' 或 'list'
  const [importModalVisible, setImportModalVisible] = useState(false);

  const [selectedPromptIds, setSelectedPromptIds] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);


  // 切换选择模式
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedPromptIds([]);
  };

  // 处理提示词选择
  const handlePromptSelect = (promptId, isSelected) => {
    if (isSelected) {
      setSelectedPromptIds(prev => [...prev, promptId]);
    } else {
      setSelectedPromptIds(prev => prev.filter(id => id !== promptId));
    }
  };

  // 全选/取消全选当前页的提示词
  const handleSelectAllCurrentPage = (isSelected) => {
    if (isSelected) {
      const currentPagePromptIds = getCurrentPagePrompts().map(prompt => prompt.id);
      setSelectedPromptIds(currentPagePromptIds);
    } else {
      setSelectedPromptIds([]);
    }
  };

  // 处理批量删除
  const handleBatchDelete = () => {
    if (selectedPromptIds.length === 0) {
      message.warning('请先选择要删除的提示词');
      return;
    }

    Modal.confirm({
      title: '确认批量删除',
      content: `您确定要删除选中的 ${selectedPromptIds.length} 个提示词吗？此操作不可恢复。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        onBatchDeletePrompts(selectedPromptIds);
        setSelectedPromptIds([]);
        setIsSelectionMode(false);
        message.success(`已成功删除 ${selectedPromptIds.length} 个提示词`);
      }
    });
  };


  // 计算当前页的提示词
  const getCurrentPagePrompts = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return prompts.slice(startIndex, endIndex);
  };

  // 处理页码变化
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 格式化日期显示
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 获取提示词所在的文件夹名称
  const getFolderName = (folderId) => {
    if (!folderId) return null;
    const folder = folders.find(f => f.id === folderId);
    return folder ? folder.name : null;
  };

  // 获取标签名称
  const getTagName = (tagId) => {
    if (!tagId) return '标签';
    // 查找标签对象
    const tag = tags.find(t => t.id === tagId);
    // 如果找到标签返回名称，否则尝试将tagId作为标签名称返回（兼容导入的标签）
    if (tag) {
      return tag.name;
    } else {
      // 检查tagId是否可能是一个标签名称而不是ID
      const isLikelyName = typeof tagId === 'string' && tagId.length < 20 && !tagId.includes('-');
      return isLikelyName ? tagId : '未知标签';
    }
  };

  // 为每个提示词创建下拉菜单
  const getPromptMenu = (promptId, prompt) => ({
    items: [
      {
        key: 'favorite',
        icon: prompt.isFavorite ? <StarFilled style={{ color: '#fadb14' }} /> : <StarOutlined />,
        label: prompt.isFavorite ? '取消收藏' : '添加收藏',
        onClick: (e) => {
          e.domEvent.stopPropagation();
          onFavoriteChange(promptId, !prompt.isFavorite);
        }
      },
      {
        key: 'view',
        icon: <EditOutlined />,
        label: '查看详情',
        onClick: (e) => {
          e.domEvent.stopPropagation();
          onSelectPrompt(promptId);
        }
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
        label: '删除提示词',
        onClick: (e) => {
          e.domEvent.stopPropagation();
          Modal.confirm({
            title: '确认删除提示词',
            content: '删除后将无法恢复，您确定要删除该提示词吗？',
            okText: '删除',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: () => {
              onDeletePrompt(promptId);
              message.success('提示词已成功删除');
            }
          });
        }
      }
    ]
  });


  // 处理导入备份
  const handleImport = (importedPrompts) => {
    // 关闭导入模态框
    setImportModalVisible(false);
    
    // 通知父组件添加导入的提示词
    if (importedPrompts && importedPrompts.length > 0) {
      // 这里需要在App.js中添加处理导入备份的方法
      if (window.addImportedPrompts) {
        window.addImportedPrompts(importedPrompts);
        message.success(`成功恢复 ${importedPrompts.length} 个提示词`);
      } else {
        console.error('未找到处理导入备份的方法');
        message.error('导入失败，请刷新页面后重试');
      }
    }
  };

  return (
    <div className="prompt-list">
      {/* 标题和创建按钮 */}
      <div className="flex justify-between items-center mb-8">
        <Title level={3} style={{ margin: 0, fontWeight: 600 }} className="text-[var(--apple-text-primary)]">我的提示词</Title>
        <Space>
          {isSelectionMode ? (
            <>
              <Button
                danger
                onClick={handleBatchDelete}
                disabled={selectedPromptIds.length === 0}
                className="btn-modern-danger"
              >
                删除选中 ({selectedPromptIds.length})
              </Button>
              <Button
                onClick={toggleSelectionMode}
                className="btn-modern-secondary"
              >
                取消选择
              </Button>
              <Checkbox
                checked={getCurrentPagePrompts().length > 0 && selectedPromptIds.length === getCurrentPagePrompts().length}
                indeterminate={selectedPromptIds.length > 0 && selectedPromptIds.length < getCurrentPagePrompts().length}
                onChange={(e) => handleSelectAllCurrentPage(e.target.checked)}
              >
                全选当前页
              </Checkbox>
            </>
          ) : (
            <>
              <Button
                icon={<ImportOutlined />}
                className="btn-modern-secondary"
                onClick={() => setImportModalVisible(true)}
              >
                导入备份
              </Button>

              {prompts.length > 0 && (
                <Button 
                  onClick={toggleSelectionMode}
                  className="btn-modern-secondary"
                >
                  批量操作
                </Button>
              )}
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={onCreatePrompt}
                className="btn-modern-primary"
              >
                创建提示词
              </Button>
              <Select 
                value={pageSize} 
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1); // 重置到第一页
                }} 
                options={[
                  { value: 9, label: '9条/页' },
                  { value: 18, label: '18条/页' },
                  { value: 36, label: '36条/页' },
                  { value: 60, label: '60条/页' },
                  { value: 100, label: '100条/页' }
                ]}
                className="w-24"
              />
              <Button 
                icon={viewMode === 'card' ? <UnorderedListOutlined /> : <AppstoreOutlined />} 
                className="btn-modern-secondary"
                onClick={() => setViewMode(viewMode === 'card' ? 'list' : 'card')}
              >
                {viewMode === 'card' ? '列表视图' : '卡片视图'}
              </Button>
            </>
          )}
        </Space>
      </div>

      {/* 提示词列表或空状态 */}
      {prompts.length === 0 ? (
        <Card variant="outlined" className="card-modern text-center p-8">
          <Empty
            image={<UnorderedListOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
            styles={{ image: { height: 80 } }}
            description={
              <Space direction="vertical" size="small">
                <Title level={4} style={{ fontWeight: 600 }}>没有提示词</Title>
                <Paragraph type="secondary">
                  创建您的第一个提示词来开始管理和版本控制您的AI提示词库
                </Paragraph>
              </Space>
            }
          >
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={onCreatePrompt}
              className="btn-modern-primary mt-4"
            >
              创建提示词
            </Button>
          </Empty>
        </Card>
      ) : (
        viewMode === 'card' ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {getCurrentPagePrompts().map(prompt => (
                <div key={prompt.id} className="hover-lift transform transition-all duration-300 hover:translate-y-[-4px]">
                  <Card
                    hoverable={!isSelectionMode}
                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                    onClick={() => !isSelectionMode && onSelectPrompt(prompt.id)}
                    className={`card-modern rounded-xl overflow-hidden ${
                      isSelectionMode && selectedPromptIds.includes(prompt.id) 
                        ? 'border-[var(--apple-accent)] border-2 shadow-accent' 
                        : 'border-subtle shadow-lg hover:shadow-xl'
                    }`}
                    extra={
                      isSelectionMode ? (
                        <Checkbox
                          checked={selectedPromptIds.includes(prompt.id)}
                          onChange={(e) => handlePromptSelect(prompt.id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <Dropdown 
                          menu={getPromptMenu(prompt.id, prompt)} 
                          trigger={['click']}
                          placement="bottomRight"
                        >
                          <Button 
                            type="text" 
                            icon={<EllipsisOutlined />} 
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[var(--apple-gray-500)] hover:text-[var(--apple-accent)] transition-colors duration-200"
                          />
                        </Dropdown>
                      )
                    }
                  >
                    {/* 卡片头部区域 - 包含标题和文件夹 */}
                    <div className="flex items-start justify-between mb-3 group">
                      <Title level={5} ellipsis={{ rows: 1 }} className="m-0 flex-1 text-[var(--apple-text-primary)] transition-colors duration-200 group-hover:text-[var(--apple-accent)]" style={{ fontWeight: 600 }}>
                        {prompt.title}
                      </Title>
                      
                      <div className="flex items-center">
                        {/* 复制按钮 - 放在卡片卡头右上角 */}
                        <Tooltip title="复制提示词内容">
                          <Button 
                            type="text"
                            size="small"
                            className="flex items-center justify-center w-8 h-8 rounded-full text-[var(--apple-text-secondary)] hover:text-[var(--apple-accent)] hover:bg-[var(--apple-bg-secondary)] transition-all duration-200"
                            icon={copiedPromptIds[prompt.id] ? <CheckOutlined className="text-green-500" /> : <CopyOutlined />}
                            onClick={(e) => {
                              e.stopPropagation(); // 阻止事件冒泡，避免触发卡片点击
                              // 获取当前版本的内容
                              const content = prompt.versions[prompt.currentVersionIndex]?.content || '';
                              // 复制到剪贴板
                              navigator.clipboard.writeText(content).then(() => {
                                // 设置复制成功状态
                                setCopiedPromptIds(prev => ({ ...prev, [prompt.id]: true }));
                                // 显示成功消息
                                message.success('提示词内容已复制到剪贴板');
                                // 2秒后重置复制状态
                                setTimeout(() => {
                                  setCopiedPromptIds(prev => {
                                    const newState = { ...prev };
                                    delete newState[prompt.id];
                                    return newState;
                                  });
                                }, 2000);
                              }).catch(err => {
                                console.error('复制失败:', err);
                                message.error('复制失败，请重试');
                              });
                            }}
                          />
                        </Tooltip>
                        
                        {/* 文件夹标识 - 更精美的设计 */}
                        {prompt.folderId && (
                          <Tooltip title="所在文件夹">
                            <div className="folder-tag ml-2 px-2 py-1 rounded-full bg-[var(--apple-bg-tertiary)] text-xs flex items-center transition-all">
                              <FolderOutlined className="mr-1 text-[var(--apple-accent)]" style={{ fontSize: '10px' }} />
                              <span className="truncate max-w-[80px] text-[var(--apple-text-secondary)]">{getFolderName(prompt.folderId)}</span>
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    
                    {/* 简介显示 */}
                    {prompt.introduction && (
                      <Paragraph ellipsis={{ rows: 2 }} className="mb-3 text-sm text-[var(--apple-text-secondary)] leading-relaxed">
                        {prompt.introduction}
                      </Paragraph>
                    )}
                    
                    {/* 内容预览 - 增加显示行数并优化样式 */}
                    <Paragraph 
                      ellipsis={{ rows: 6 }} 
                      className="mb-4 text-[var(--apple-text-primary)] bg-[var(--apple-bg-secondary)] p-4 rounded-xl text-sm font-mono leading-relaxed shadow-inner"
                      style={{ backdropFilter: 'blur(8px)' }}
                    >
                      {prompt.versions[prompt.currentVersionIndex]?.content || ''}
                    </Paragraph>
                    
                    {/* 卡片底部 - 元数据区域 */}
                    <div className="mt-auto pt-3 border-t border-[var(--apple-divider)]">
                      <Space size={[0, 8]} wrap>
                        {/* 标签展示 */}
                        {prompt.tags && prompt.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {prompt.tags.map(tagId => (
                              <Tag 
                                key={tagId} 
                                className="tag-modern rounded-full border-none px-3 py-1 text-xs transition-all hover:scale-105"
                                color={`var(--tag-color-${Math.abs(tagId.toString().charCodeAt(0) % 10)})`}
                              >
                                {getTagName(tagId)}
                              </Tag>
                            ))}
                          </div>
                        )}
                        
                        {/* 时间信息展示 */}
                        <div className="flex items-center justify-between w-full text-xs text-[var(--apple-text-tertiary)] mt-2">
                          <Tooltip title="创建时间" placement="bottom">
                            <div className="flex items-center px-2 py-1 rounded-full bg-[var(--apple-bg-tertiary)] opacity-80 hover:opacity-100 transition-opacity">
                              <ClockCircleOutlined className="mr-1" />
                              <span>{formatDate(prompt.createdAt)}</span>
                            </div>
                          </Tooltip>
                          
                          <Tooltip title="历史版本" placement="bottom">
                            <div className="flex items-center px-2 py-1 rounded-full bg-[var(--apple-bg-tertiary)] opacity-80 hover:opacity-100 transition-opacity">
                              <HistoryOutlined className="mr-1" />
                              <span>{prompt.versions.length}</span>
                            </div>
                          </Tooltip>
                        </div>
                      </Space>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
            
            {/* 分页 */}
            {prompts.length > pageSize && (
              <div className="flex justify-center mt-10 mb-4">
                <Pagination 
                  current={currentPage} 
                  total={prompts.length} 
                  pageSize={pageSize} 
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  className="pagination-modern"
                />
              </div>
            )}
          </>
        ) : (
          <Table
            columns={[
              {
                title: '标题',
                dataIndex: 'title',
                key: 'title',
                render: (title, prompt) => (
                  <div className="flex items-start">
                    <div className="flex-1">
                      <div className="text-base font-medium text-[var(--apple-text-primary)] whitespace-pre-wrap break-words hover:text-[var(--apple-accent)] transition-colors duration-200" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                        {prompt.isFavorite && <StarFilled style={{ color: '#fadb14', marginRight: '8px' }} />}
                        {title}
                      </div>
                    </div>
                    <div className="flex items-center">
                      {/* 列表视图中的复制按钮 */}
                      <Tooltip title="复制提示词内容">
                        <Button 
                          type="text"
                          size="small"
                          className="flex items-center justify-center w-8 h-8 rounded-full text-[var(--apple-text-secondary)] hover:text-[var(--apple-accent)] hover:bg-[var(--apple-bg-secondary)] transition-all duration-200"
                          icon={copiedPromptIds[prompt.id] ? <CheckOutlined className="text-green-500" /> : <CopyOutlined />}
                          onClick={(e) => {
                            e.stopPropagation(); // 阻止事件冒泡
                            // 获取当前版本的内容
                            const content = prompt.versions[prompt.currentVersionIndex]?.content || '';
                            // 复制到剪贴板
                            navigator.clipboard.writeText(content).then(() => {
                              // 设置复制成功状态
                              setCopiedPromptIds(prev => ({ ...prev, [prompt.id]: true }));
                              // 显示成功消息
                              message.success('提示词内容已复制到剪贴板');
                              // 2秒后重置复制状态
                              setTimeout(() => {
                                setCopiedPromptIds(prev => {
                                  const newState = { ...prev };
                                  delete newState[prompt.id];
                                  return newState;
                                });
                              }, 2000);
                            }).catch(err => {
                              console.error('复制失败:', err);
                              message.error('复制失败，请重试');
                            });
                          }}
                        />
                      </Tooltip>
                      {prompt.folderId && (
                        <Tooltip title="所在文件夹">
                          <div className="folder-tag ml-2 mt-1 px-2 py-1 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 text-xs flex items-center transition-all whitespace-nowrap border border-blue-100 shadow-sm hover:shadow-md hover:from-blue-100 hover:to-indigo-100">
                            <FolderOutlined className="mr-1 text-[var(--apple-accent)]" style={{ fontSize: '10px' }} />
                            <span className="truncate max-w-[80px] text-indigo-600">{getFolderName(prompt.folderId)}</span>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                ),
                width: '25%'
              },
              {
                title: '简介',
                dataIndex: 'introduction',
                key: 'introduction',
                render: (introduction) => (
                  <div className="text-sm text-[var(--apple-text-secondary)] whitespace-pre-wrap break-words bg-gradient-to-r from-gray-50 to-white p-3 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300" style={{ lineHeight: 1.5, maxHeight: '80px', overflow: 'auto' }}>
                    {introduction || <span className="text-gray-400 italic">暂无简介</span>}
                  </div>
                ),
                width: '30%'
              },
              {
                title: '内容预览',
                dataIndex: 'versions',
                key: 'versions',
                render: (versions, prompt) => (
                  <Paragraph 
                    ellipsis={{ rows: 3 }} 
                    className="mb-2 text-[var(--apple-text-primary)] bg-gradient-to-r from-gray-50 to-white p-3 rounded-lg text-sm font-mono border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    {versions[prompt.currentVersionIndex]?.content || <span className="text-gray-400 italic">暂无内容</span>}
                  </Paragraph>
                ),
                width: '25%'
              },
              {
                title: '标签',
                dataIndex: 'tags',
                key: 'tags',
                render: (tags) => (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {tags && tags.length > 0 ? tags.map(tagId => (
                      <Tag 
                        key={tagId} 
                        className="tag-modern rounded-full border-none px-3 py-1 text-xs transition-all hover:scale-105 shadow-sm"
                        color={`var(--tag-color-${Math.abs(tagId.toString().charCodeAt(0) % 10)})`}
                      >
                        {getTagName(tagId)}
                      </Tag>
                    )) : (
                      <span className="text-gray-400 italic text-xs">暂无标签</span>
                    )}
                  </div>
                ),
                width: '15%'
              },
              {
                title: <span className="whitespace-nowrap">历史版本</span>,
                dataIndex: 'versions',
                key: 'versions',
                render: (versions) => (
                  <div className="flex items-center justify-center text-sm text-[var(--apple-text-secondary)]">
                    <span className="font-medium">{versions.length}</span>
                  </div>
                ),
                width: '10%'
              },
              {
                title: '操作',
                dataIndex: 'id',
                key: 'id',
                render: (id, prompt) => (
                  <Space size="small">
                    <Button 
                      type="primary" 
                      icon={<EditOutlined />} 
                      size="small"
                      onClick={() => onSelectPrompt(id)}
                      className="bg-gradient-to-r from-blue-500 to-indigo-500 border-none shadow-sm hover:shadow-md transition-all duration-300 hover:from-blue-600 hover:to-indigo-600"
                    >
                      查看详情
                    </Button>
                    <Dropdown 
                      menu={getPromptMenu(id, prompt)} 
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <Button 
                        type="text" 
                        icon={<EllipsisOutlined />} 
                        size="small"
                        className="text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200 rounded-full"
                      />
                    </Dropdown>
                  </Space>
                ),
                width: '15%'
              }
            ]}
            dataSource={getCurrentPagePrompts()}
            rowKey="id"
            pagination={false}
            onRow={(record) => ({
              onClick: () => !isSelectionMode && onSelectPrompt(record.id),
              className: `cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${
                isSelectionMode && selectedPromptIds.includes(record.id) 
                  ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                  : ''
              }`
            })}
            rowSelection={isSelectionMode ? {
              selectedRowKeys: selectedPromptIds,
              onChange: (selectedRowKeys) => {
                setSelectedPromptIds(selectedRowKeys);
              }
            } : null}
            className="table-modern rounded-xl overflow-hidden shadow-lg"
            style={{ 
              background: 'white',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          />
        )
      )}
      
      {/* 导入备份模态框 */}
      <Modal
        title="导入备份"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={700}
        destroyOnClose={true}
      >
        <ImportPrompts 
          onImport={handleImport}
          folders={folders}
          onAddFolder={onAddFolder}
          tags={tags}
          onAddTag={onAddTag}
        />
      </Modal>
    </div>
  );
}

export default PromptList;
